import {config} from "@shared/_services/config";
import {HTTP} from "../_helpers/http";

export default class FileApi {
    // Legacy image API methods (kept for backward compatibility)
    static fetchImages() {
        return HTTP.get(config.fileApi.image)
            .then(response => response.data);
    }

    static upload(file) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return HTTP.post(config.fileApi.image, formData)
            .then(response => response.data);
    }

    // New Media Management API methods
    /**
     * Get all media files with pagination, filtering, and search
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default: 0)
     * @param {number} params.size - Page size (default: 12)
     * @param {string} params.type - Media type filter (IMAGE, VIDEO, DOCUMENT)
     * @param {string} params.search - Search query
     * @param {string} params.sortBy - Sort field (default: createdAt)
     * @param {string} params.sortDir - Sort direction (asc/desc, default: desc)
     */
    static fetchMedia(params = {}) {
        const queryParams = new URLSearchParams();

        if (params.page !== undefined) queryParams.append('page', params.page);
        if (params.size !== undefined) queryParams.append('size', params.size);
        if (params.type) queryParams.append('type', params.type);
        if (params.search) queryParams.append('search', params.search);
        if (params.sortBy) queryParams.append('sortBy', params.sortBy);
        if (params.sortDir) queryParams.append('sortDir', params.sortDir);

        const url = `${config.fileApi.media}?${queryParams.toString()}`;
        return HTTP.get(url).then(response => response.data);
    }

    /**
     * Get a specific media file by ID
     * @param {number} id - Media file ID
     */
    static getMediaById(id) {
        return HTTP.get(`${config.fileApi.media}/${id}`)
            .then(response => response.data);
    }

    /**
     * Upload a new media file
     * @param {File} file - File to upload
     */
    static uploadMedia(file) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return HTTP.post(`${config.fileApi.media}/upload`, formData)
            .then(response => response.data);
    }

    /**
     * Update media file metadata
     * @param {number} id - Media file ID
     * @param {Object} data - Update data
     * @param {string} data.filename - New filename
     * @param {string} data.description - Description
     * @param {string} data.tags - Tags (comma-separated)
     */
    static updateMedia(id, data) {
        return HTTP.put(`${config.fileApi.media}/${id}`, data)
            .then(response => response.data);
    }

    /**
     * Delete a media file
     * @param {number} id - Media file ID
     */
    static deleteMedia(id) {
        return HTTP.delete(`${config.fileApi.media}/${id}`)
            .then(response => response.data);
    }

    /**
     * Replace the physical file of an existing media record
     * Keeps the same filename to maintain all existing references
     */
    static replaceMedia(id, file) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return HTTP.post(`${config.fileApi.media}/${id}/replace`, formData)
            .then(response => response.data);
    }

    /**
     * Generate JSON reference file for ERP integration
     * Downloads a JSON file that can be uploaded to ERP article files folder
     * @param {number} id - Media file ID
     */
    static generateReference(id) {
        return HTTP.get(`${config.fileApi.media}/${id}/generate-reference`, {
            responseType: 'blob'
        }).then(response => {
            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'media_ref.json';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true, filename };
        });
    }

    /**
     * Migrate existing files from upload folder
     */
    static migrateExistingFiles() {
        return HTTP.post(`${config.fileApi.media}/migration/migrate`)
            .then(response => response.data);
    }
}